import React, { useState, useEffect, useRef } from 'react';
import './ImageDropdown.css';
import { matchesSearch } from '../utils/search';

/**
 * Searchable dropdown with same look/behavior as ImageDropdown, ColorDropdown.
 * options: array of { key, value, valueDisplay? }; valueDisplay optional second line (defaults to value).
 */
const SearchableDropdown = ({
  options = [], value, onChange, placeholder = 'Select...',
  id, disabled = false, error = false, 'aria-label': ariaLabel
}) => {
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    if (!searchTerm) setFilteredOptions(options);
    else {
      setFilteredOptions(options.filter(
        (opt) => matchesSearch(searchTerm, opt.key, opt.value, opt.valueDisplay)
      ));
    }
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (opt, e) => {
    // preventDefault: options sit inside <label>s in AgentEditor; without this the
    // label re-focuses the input and onClick reopens the menu immediately.
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (disabled) return;
    onChange(opt.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  const getDisplayValue = () => {
    const selected = options.find((o) => o.value === value);
    if (!selected) return value ? String(value) : '';
    const sub = selected.valueDisplay;
    // Only show "key (sub)" when valueDisplay is an explicit secondary line.
    if (sub != null && sub !== '' && String(sub) !== String(selected.key)) {
      return `${selected.key} (${sub})`;
    }
    return selected.key;
  };

  return (
    <div className="image-dropdown" ref={dropdownRef}>
      <input
        type="text"
        id={id}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={error || undefined}
        onFocus={() => setIsOpen(true)}
        onBlur={() => { setIsOpen(false); setSearchTerm(''); }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setIsOpen(false); setSearchTerm(''); }
          if (e.key === 'Enter' && isOpen) {
            e.preventDefault();
            if (filteredOptions.length === 1) handleSelect(filteredOptions[0], e);
          }
        }}
        value={isOpen && !disabled ? searchTerm : getDisplayValue()}
        onChange={(e) => { setSearchTerm(e.target.value); setIsOpen(true); }}
        onClick={() => setIsOpen(true)}
        placeholder={placeholder}
        className="image-dropdown-input"
        readOnly={!isOpen}
      />
      {isOpen && !disabled && (
        <div className="image-dropdown-menu">
          {filteredOptions.length === 0 ? (
            <div className="dropdown-no-results">No results found</div>
          ) : (
            filteredOptions.map((opt) => (
              <div
                key={String(opt.value) || opt.key}
                className="dropdown-option"
                onMouseDown={(e) => handleSelect(opt, e)}
              >
                <div className="image-key">{opt.key}</div>
                <div className="image-value">{opt.valueDisplay ?? opt.value}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
